// src/pages/VerifyAccountLink.jsx
//
// Public page — no authentication required. Reached by clicking the
// "Verify my email →" button in the link-based verification email (see
// POST /api/admin/firms/{id}/send-verification-link, backend/emails/
// sender.py send_email_verification_link). A second, lower-friction
// channel alongside VerifyEmailPending.jsx's 6-digit code — no code to
// type, the token in the URL itself is the credential, verified
// server-side (GET /api/auth/verify-email-link/{token}). See
// kailux-fixes-log.md §57 for why this was built: a signup-time code
// alone left several real firms permanently stuck unverified after a
// single missed 10-minute window, with no reminder ever following up.
//
// Deliberately outside ProtectedRoute — mirrors /signoff/:token and
// /intake/:token: the person clicking this link has no session yet
// (or may never have logged in on this device before), so this can't
// require auth the way the rest of the app does.
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { verifyEmailLink } from '../api/kailux'
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react'


import { T } from '../theme/theme'
export default function VerifyAccountLink() {
  const { token } = useParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState('checking') // checking | success | already | error
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    let mounted = true
    verifyEmailLink(token)
      .then((res) => {
        if (!mounted) return
        setStatus(res.already_verified ? 'already' : 'success')
      })
      .catch((err) => {
        if (!mounted) return
        setStatus('error')
        setErrorMsg(err.response?.data?.detail || 'This verification link could not be used. Please request a new one.')
      })
    return () => { mounted = false }
  }, [token])

  return (
    <div style={{ minHeight:'100vh', background:T.bg, color:T.tx,
      fontFamily:"'Outfit',sans-serif", display:'flex', alignItems:'center',
      justifyContent:'center', padding:'24px' }}>
      <div style={{ width:'100%', maxWidth:'480px', background:T.s1,
        border:`1px solid ${T.bd}`, borderRadius:'16px', padding:'40px',
        textAlign:'center' }}>

        {status === 'checking' && (
          <>
            <Loader2 size={40} color={T.or} style={{ animation:'spin 1s linear infinite' }} />
            <p style={{ fontSize:'15px', color:T.mt, marginTop:'16px' }}>Verifying your email…</p>
          </>
        )}

        {(status === 'success' || status === 'already') && (
          <>
            <CheckCircle2 size={48} color={T.gn} style={{ marginBottom:'16px' }} />
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'12px' }}>
              Your account is successfully verified
            </div>
            <p style={{ fontSize:'14px', fontWeight:'400', color:T.lt, lineHeight:'1.7', marginBottom:'28px' }}>
              You're all set. Log in, complete payment, and generate your AFS.
            </p>
            <button onClick={() => navigate('/login')}
              style={{ padding:'14px 32px', borderRadius:'8px', border:'none',
                background:T.prg,
                color:T.onpri, fontSize:'15px', fontWeight:'700', cursor:'pointer' }}>
              Log in →
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle size={48} color={T.rd} style={{ marginBottom:'16px' }} />
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px', fontWeight:'800', marginBottom:'12px' }}>
              Link no longer valid
            </div>
            <p style={{ fontSize:'14px', fontWeight:'700', color:T.rd, lineHeight:'1.7', marginBottom:'28px' }}>
              {errorMsg}
            </p>
            <Link to="/login" style={{ fontSize:'14px', color:T.or, textDecoration:'underline' }}>
              Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
